import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBullhorn,
  FaChartColumn,
  FaDownload,
  FaEye,
  FaFloppyDisk,
  FaKey,
  FaLightbulb,
  FaRobot,
  FaWandMagicSparkles,
} from "react-icons/fa6";
import { css } from "../../../../styled-system/css";
import { Box, Flex, Grid } from "../../../../styled-system/jsx";
import { Modal } from "../../../components/core/basic/Modal";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import type {
  ActivityKind,
  PersonEvent,
  UserActivityReport,
} from "../../../zodios/apiDefinitions/api/staffUserActivity";
import { type ActivityPeriod, usePersonEvents } from "../api/useUserActivity";
import { eachDay, featureOf, type FeatureId, type PersonStats } from "../model";
import { TEAM_COLOR, TRELLIS_COLOR } from "../theme";
import { useActivityLabels } from "../useActivityLabels";
import { EmptyNote } from "./ActivityCharts";
import { Pill, StatusBadge, useCount, useRelativeTime } from "./primitives";
import { Spinner } from "./ReportLoading";

const KIND_ICONS: Record<ActivityKind, ReactNode> = {
  page_view: <FaEye />,
  campaign_change: <FaBullhorn />,
  keyword_change: <FaKey />,
  rule_change: <FaRobot />,
  export: <FaDownload />,
  saved_view: <FaFloppyDisk />,
  insight: <FaLightbulb />,
  amc_report: <FaChartColumn />,
  creative: <FaWandMagicSparkles />,
};

function useTopFeatures(report: UserActivityReport, userId: number) {
  return useMemo(() => {
    const days = new Set(eachDay(report.period.start, report.period.end));
    const counts = new Map<FeatureId, number>();
    for (const row of report.activity) {
      if (row.user_id !== userId || !days.has(new Date(row.hour).toISOString().slice(0, 10))) continue;
      const feature = featureOf(row);
      if (feature) counts.set(feature, (counts.get(feature) ?? 0) + row.count);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [report, userId]);
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Box borderRadius="light" padding="small" backgroundColor="#f7f5fa">
      <span className={css({ display: "block", color: "text.secondary", fontSize: "small" })}>{label}</span>
      <span className={css({ color: "text.heading", fontSize: "xxlarge", fontWeight: "700" })}>{value}</span>
    </Box>
  );
}

function Timeline({ events }: { events: PersonEvent[] }) {
  const labels = useActivityLabels();
  const dayHeading = useDateFormatter("long");
  const { i18n } = useTranslation();
  const timeFormat = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { hour: "numeric", minute: "2-digit" }),
    [i18n.language],
  );

  const byDay = useMemo(() => {
    const groups = new Map<string, PersonEvent[]>();
    for (const event of events) {
      const day = new Date(event.ts).toDateString();
      groups.set(day, [...(groups.get(day) ?? []), event]);
    }
    return [...groups.entries()];
  }, [events]);

  return (
    <Flex direction="column" gap="medium">
      {byDay.map(([day, dayEvents]) => (
        <Box key={day}>
          <h4
            className={css({
              position: "sticky",
              zIndex: 1,
              top: 0,
              margin: 0,
              paddingY: "xsmall",
              color: "text.secondary",
              fontSize: "small",
              fontWeight: "700",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              backgroundColor: "white",
            })}
          >
            {dayHeading.format(new Date(day))}
          </h4>
          <ol className={css({ margin: 0, padding: 0 })}>
            {dayEvents.map((event, index) => (
              <li
                key={`${event.ts}-${index}`}
                className={css({
                  display: "flex",
                  gap: "small",
                  alignItems: "flex-start",
                  borderBottom: "1px solid #f0eef4",
                  paddingY: "small",
                  listStyle: "none",
                })}
              >
                <span
                  aria-hidden
                  className={css({
                    display: "inline-flex",
                    flexShrink: 0,
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "pill",
                    width: "28px",
                    height: "28px",
                    fontSize: "small",
                    backgroundColor: "#f1edf8",
                  })}
                  style={{ color: TEAM_COLOR }}
                >
                  {KIND_ICONS[event.kind]}
                </span>
                <Box flex="1" minWidth="0">
                  <span className={css({ display: "block", color: "text.heading", overflowWrap: "anywhere" })}>
                    {labels.event(event)}
                  </span>
                  {event.campaign && (
                    <span className={css({ color: "text.secondary", fontSize: "small", overflowWrap: "anywhere" })}>
                      {event.campaign}
                    </span>
                  )}
                </Box>
                <time
                  dateTime={event.ts}
                  className={css({ flexShrink: 0, color: "text.secondary", fontSize: "small", whiteSpace: "nowrap" })}
                >
                  {timeFormat.format(new Date(event.ts))}
                </time>
              </li>
            ))}
          </ol>
        </Box>
      ))}
    </Flex>
  );
}

export function PersonModal({
  stats,
  report,
  period,
  onClose,
}: {
  stats: PersonStats | undefined;
  report: UserActivityReport;
  period: ActivityPeriod;
  onClose: () => void;
}) {
  const { t } = useTranslation(["user_activity"]);
  const labels = useActivityLabels();
  const count = useCount();
  const relativeTime = useRelativeTime();
  const activity = usePersonEvents(report.org.id, stats?.person.user_id ?? null, period);
  const topFeatures = useTopFeatures(report, stats?.person.user_id ?? -1);

  if (!stats) return null;
  const { person } = stats;
  const days = eachDay(period.start, period.end).length;
  const color = person.is_trellis ? TRELLIS_COLOR : TEAM_COLOR;
  const topCount = topFeatures[0]?.[1] ?? 1;

  return (
    <Modal
      isOpen
      isDismissable
      onOpenChange={(open) => !open && onClose()}
      css={{ width: "min(760px, 94vw)", maxWidth: "94vw", borderRadius: "standard", paddingX: "large" }}
    >
      <Flex direction="column" gap="medium" overflowY="auto" minHeight="0" paddingRight="small">
        <Flex gap="medium" alignItems="center" paddingRight="xlarge">
          <Box
            aria-hidden
            display="flex"
            flexShrink={0}
            justifyContent="center"
            alignItems="center"
            borderRadius="pill"
            width="56px"
            height="56px"
            color="white"
            fontSize="xlarge"
            fontWeight="700"
            style={{ backgroundColor: color }}
          >
            {person.name
              .split(/\s+/)
              .map((part) => part[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </Box>
          <Box minWidth="0">
            <h3 className={css({ margin: 0, color: "text.heading", fontSize: "xxlarge", fontWeight: "700" })}>
              {person.name}
            </h3>
            <Flex gap="small" alignItems="center" flexWrap="wrap" color="text.secondary" fontSize="small">
              <span>{person.email}</span>
              {person.role && <Pill>{person.role.replaceAll("_", " ")}</Pill>}
              {person.is_trellis && <Pill tone="brand">{t(($) => $.user_activity["Trellis"])}</Pill>}
              <StatusBadge status={stats.status} />
            </Flex>
          </Box>
        </Flex>

        <Grid gridTemplateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap="small">
          <Stat
            label={t(($) => $.user_activity["Last seen"])}
            value={stats.lastSeen ? relativeTime(stats.lastSeen) : t(($) => $.user_activity["Never"])}
          />
          <Stat
            label={t(($) => $.user_activity["Days active"])}
            value={t(($) => $.user_activity["{active} of {days}"], { active: stats.activeDays, days })}
          />
          <Stat label={t(($) => $.user_activity["Changes"])} value={count.whole(stats.current.changes)} />
          <Stat label={t(($) => $.user_activity["Pages viewed"])} value={count.whole(stats.current.views)} />
        </Grid>

        {topFeatures.length > 0 && (
          <Box>
            <h4 className={css({ margin: 0, marginBottom: "small", color: "text.heading", fontSize: "large" })}>
              {t(($) => $.user_activity["What they use most"])}
            </h4>
            <Flex direction="column" gap="xsmall">
              {topFeatures.map(([feature, actions]) => (
                <Grid
                  key={feature}
                  gridTemplateColumns="180px 1fr 60px"
                  gap="small"
                  alignItems="center"
                  fontSize="small"
                >
                  <span className={css({ color: "text.heading" })}>{labels.feature(feature)}</span>
                  <Box height="8px" borderRadius="pill" backgroundColor="#f1edf8" overflow="hidden">
                    <Box
                      height="100%"
                      borderRadius="pill"
                      style={{ width: `${(actions / topCount) * 100}%`, backgroundColor: color }}
                    />
                  </Box>
                  <span
                    className={css({ color: "text.secondary", textAlign: "end", fontVariantNumeric: "tabular-nums" })}
                  >
                    {count.whole(actions)}
                  </span>
                </Grid>
              ))}
            </Flex>
          </Box>
        )}

        <Box>
          <h4 className={css({ margin: 0, marginBottom: "small", color: "text.heading", fontSize: "large" })}>
            {t(($) => $.user_activity["Recent activity"])}
          </h4>
          {activity.isFailed ?
            <EmptyNote>
              {t(($) => $.user_activity["Couldn't load this person's activity. Try again in a moment."])}
            </EmptyNote>
          : !activity.events ?
            <Flex direction="column" gap="small" aria-busy>
              <Flex gap="small" alignItems="center" color="text.secondary" fontSize="small">
                <Spinner size={16} />
                <span role="status">
                  {t(($) => $.user_activity["Loading their recent activity. This can take a moment."])}
                </span>
              </Flex>
              {Array.from({ length: 5 }, (_, index) => (
                <Box key={index} height="36px" borderRadius="light" style={{ backgroundColor: "#f4f2f7" }} />
              ))}
            </Flex>
          : activity.events.events.length > 0 ?
            <>
              <Timeline events={activity.events.events} />
              {activity.events.events.length >= activity.events.limit && (
                <p className={css({ margin: 0, marginTop: "small", color: "text.secondary", fontSize: "small" })}>
                  {t(($) => $.user_activity["Showing the most recent {limit} actions."], {
                    limit: activity.events.limit,
                  })}
                </p>
              )}
            </>
          : <EmptyNote>{t(($) => $.user_activity["Nothing recorded for this person in this period."])}</EmptyNote>}
        </Box>
      </Flex>
    </Modal>
  );
}
